import { z } from "zod";
const ERROR_CODES = [
  // Generic
  "BAD_REQUEST",
  "VALIDATION_FAILED",
  "NOT_FOUND",
  "PAYLOAD_TOO_LARGE",
  "UNSUPPORTED_MEDIA_TYPE",
  "RATE_LIMITED",
  "INTERNAL_ERROR",
  "SERVICE_UNAVAILABLE",
  // Authentication and session
  "SESSION_EXPIRED",
  "CSRF_INVALID",
  "MFA_REQUIRED",
  "STEP_UP_REQUIRED",
  // Authorization, plan, organisation state
  "FORBIDDEN",
  "PLAN_LIMIT_REACHED",
  "ORGANIZATION_READ_ONLY",
  // Concurrency and idempotency
  "STALE_VERSION",
  "PRECONDITION_REQUIRED",
  "IDEMPOTENCY_KEY_REUSED",
  // Files
  "FILE_TYPE_NOT_ALLOWED",
  "FILE_TOO_LARGE",
  "QUOTA_EXCEEDED",
  // Business conflicts
  "PHASE_HAS_UNAPPROVED_DELIVERABLES",
  "INVOICE_ALREADY_ISSUED",
  "COMPUTATION_CHANGED",
  "DOCUMENT_LOCKED",
  "LEGAL_HOLD",
  "DEPENDENCY_CYCLE",
  // Organisations and members (ORG)
  "TAX_ID_ALREADY_REGISTERED",
  "ALREADY_A_MEMBER",
  "INVITATION_ALREADY_PENDING",
  "INVITATION_NO_LONGER_VALID",
  // Custom roles (ORGX): a key the caller does not hold; a role still given to someone
  "ROLE_ESCALATION",
  "ROLE_IN_USE",
  // Timesheets (PLN): a submitted week or a decided entry
  "TIMESHEET_LOCKED",
  "INVITATION_EMAIL_MISMATCH",
  "EMAIL_NOT_VERIFIED",
  // Projects (PRJ)
  "PROJECT_CODE_TAKEN",
  "PHASE_CLOSED",
  "PROJECT_ARCHIVED",
  "PROJECT_TEMPLATE_INVALID",
  // Documents (GED)
  "FOLDER_NAME_TAKEN",
  "UPLOAD_EXPIRED",
  "DOWNLOAD_NOT_ALLOWED",
  // GEDX-BE-04: share links (the space forbids them or a setting exceeds its policy; password).
  "SHARE_LINKS_DISABLED",
  "SHARE_LINK_POLICY",
  "SHARE_LINK_PASSWORD_INVALID",
  "SHARE_LINK_LOCKED",
  "SHARE_LINK_DOWNLOAD_LIMIT",
  // COLX: a synthesis needs a shared space linked to a project; a groupement's shares; an exit
  // export window; an organisation step of an approval template with nobody to approve.
  "SPACE_WITHOUT_PROJECT",
  "GROUPEMENT_SHARES_EXCEED",
  "EXIT_EXPORT_UNAVAILABLE",
  "APPROVAL_ORG_HAS_NO_REVIEWER",
  "ANNOTATION_RESOLVED",
  "APPROVAL_CLOSED",
  "APPROVAL_NOT_YOUR_TURN",
  "PARENT_IN_TRASH",
  // Messaging (COM)
  "CHANNEL_ARCHIVED",
  "CHANNEL_NAME_TAKEN",
  // Calendar (COM)
  "EVENT_READ_ONLY",
  // Meetings (COM; INVITATION_EMAIL_MISMATCH is shared with the organisation invitations)
  "MEETING_ENDED",
  // Meeting minutes (COMX): one PV per meeting, none for a call, a published PV never changes
  "MINUTES_EXIST",
  "MINUTES_NOT_FOR_CALLS",
  "MINUTES_PUBLISHED",
  // Sharing (COL)
  "SPACE_CLOSED",
  "PARTNERSHIP_ALREADY_EXISTS",
  "MEMBER_CAP_REACHED",
  "SCOPE_EXCEEDS_PARTNERSHIP",
  "RIGHTS_EXCEED_PARTNERSHIP",
  "DELEGATE_NOT_A_MEMBER",
  "ALREADY_SPACE_MEMBER",
  "GUEST_DOMAIN_NOT_ALLOWED",
  // Finance (FIN)
  "QUOTE_LOCKED",
  "QUOTE_EXPIRED",
  "QUOTE_NOT_ACCEPTED",
  "CONTRACT_REFERENCE_TAKEN",
  "CONTRACT_EXISTS_FOR_QUOTE",
  "PDF_NOT_READY",
  "INVOICE_NOT_ISSUED",
  "CREDIT_EXCEEDS_INVOICE",
  "OVERPAYMENT",
  "EXPENSE_INVALID_TRANSITION",
  "EXPENSE_SELF_APPROVAL",
  "RECEIPT_NOT_READY",
  // E-invoicing (EINV)
  "SIGNING_CREDENTIAL_INVALID",
  "SIGNING_KEY_MISMATCH",
  "SIGNING_CREDENTIAL_EXPIRED",
  "SIGNING_CREDENTIAL_DUPLICATE",
  "EINVOICE_NOT_RESUBMITTABLE",
  "EINVOICE_ALREADY_FINAL",
  "EINVOICE_NOT_REQUIRED",
  "TEIF_NOT_READY",
  // Subscription billing (BILL)
  "PLAN_ON_QUOTE",
  "SUBSCRIPTION_DOWNGRADE_BLOCKED",
  "SUBSCRIPTION_INVALID_STATE",
  "SUBSCRIPTION_INVOICE_PENDING",
  "PAUSE_LIMIT_REACHED",
  "BILLING_INVOICE_NOT_OPEN",
  "PAYMENT_PROVIDER_UNAVAILABLE",
  "WEBHOOK_SIGNATURE_INVALID",
  // Back-office (PLT).
  "WEBAUTHN_REQUIRED",
  "BREAK_GLASS_SELF_APPROVAL",
  "BREAK_GLASS_INVALID_STATE",
  "SUPPORT_IDENTITY_MISSING",
  "PAYMENT_ALREADY_CONFIRMED",
  "ORGANIZATION_CLOSED",
  // Privacy (REL-BE-01)
  "EXPORT_REQUIRED",
  "EXPORT_NOT_READY",
  "EXPORT_IN_PROGRESS",
  "DELETION_ALREADY_SCHEDULED",
  "DELETION_NOT_SCHEDULED",
  "CONFIRMATION_MISMATCH",
  // Regulatory checklists (GEDX): an archived checklist is read-only; a retired template version
  // cannot be edited or used; one live organisation template per key
  "CHECKLIST_ARCHIVED",
  "CHECKLIST_TEMPLATE_RETIRED",
  "CHECKLIST_TEMPLATE_KEY_TAKEN",
  // Tenders and the vault (AO): a status the pipeline does not allow; a tender that cannot be
  // deleted or converted; a vault document or file that is unknown, invalid or still in use.
  "TENDER_STATUS_TRANSITION",
  "TENDER_NOT_DELETABLE",
  "TENDER_NOT_WON",
  "VAULT_DOCUMENT_UNKNOWN",
  "VAULT_DOCUMENT_FILE_INVALID",
  "VAULT_DOCUMENT_IN_USE",
  // Site observations (SITE): a client id already used by another project or author; a plan point
  // or a photo that is not valid for the project; an observation that is not open (or not
  // resolved); a reception report whose PDF is still being made.
  "OBSERVATION_ID_TAKEN",
  "OBSERVATION_ANCHOR_INVALID",
  "OBSERVATION_PHOTO_INVALID",
  "OBSERVATION_NOT_OPEN",
  "OBSERVATION_NOT_RESOLVED",
  "OBSERVATION_IN_REPORT",
  // FINX-BE-01: suppliers, supplier invoices and payments, TEJ exports.
  "SUPPLIER_TAX_ID_TAKEN",
  "SUPPLIER_ARCHIVED",
  "SUPPLIER_INVOICE_NUMBER_TAKEN",
  "SUPPLIER_INVOICE_NOT_PAYABLE",
  "SUPPLIER_INVOICE_HAS_PAYMENTS",
  "SUPPLIER_PAYMENT_EXCEEDS",
  "SUPPLIER_PAYMENT_EXPORTED",
  "SUPPLIER_DOCUMENT_INVALID",
  "TEJ_NOTHING_TO_EXPORT",
  // FINX-BE-03: a closed accounting month.
  "PERIOD_CLOSED",
  // FINX-BE-02: one rate per person and date.
  "COST_RATE_DATE_TAKEN",
  // FINX-BE-03.
  "ACCOUNTING_EXPORT_NOT_READY",
  "PERIOD_IN_FUTURE",
  // SIGN: electronic signature.
  "SIGNATURE_NOT_PENDING",
  "SIGNATURE_EXPIRED",
  "SIGNATURE_CODE_INVALID",
  "SIGNATURE_CODE_LOCKED",
  "SIGNATURE_CODE_TOO_SOON",
  "SIGNATURE_DOCUMENT_NOT_READY",
  "SIGNATURE_SIGNER_INVALID",
  "SIGNATURE_EVIDENCE_NOT_READY",
  "SIGNATURE_PROVIDER_FAILED",
  "DIGIGO_NOTHING_TO_SIGN",
  "DIGIGO_NO_CREDENTIAL",
  "SIGNING_BATCH_NOT_PENDING",
  "RECEPTION_REPORT_NOT_READY",
  // Platform user management (2026-10-30): a membership that is not in the state the action needs
  // (suspended members accept no invitation); the owner is protected (owner transfer first); an
  // account that cannot be deleted yet (support identity); the platform's own organisation.
  "MEMBERSHIP_SUSPENDED",
  "MEMBER_STATE_INVALID",
  "OWNER_PROTECTED",
  "ACCOUNT_NOT_DELETABLE",
  "PLATFORM_ORGANIZATION_PROTECTED",
  // Four-eyes organisation deletion: one pending request per organisation, decided by another
  // admin; a pending deletion freezes the subscription override.
  "DELETION_ALREADY_REQUESTED",
  "DELETION_SELF_APPROVAL",
  "DELETION_INVALID_STATE",
  "DELETION_PENDING",
  // Account security in the app (/me/security): a fresh sign-in is needed; the password was
  // refused by the realm's policy; the last second factor cannot go while two-step is required.
  "REAUTH_REQUIRED",
  "PASSWORD_POLICY",
  "LAST_SECOND_FACTOR"
];
const ErrorCodeSchema = z.enum(ERROR_CODES);
const FieldErrorSchema = z.strictObject({
  path: z.string(),
  code: z.string(),
  message: z.string()
});
const ProblemDetailsSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.int().min(400).max(599),
  code: ErrorCodeSchema,
  detail: z.string().optional(),
  instance: z.string().optional(),
  traceId: z.string(),
  errors: z.array(FieldErrorSchema).optional(),
  /** PLAN_LIMIT_REACHED only: the feature the plan lacks (an entitlement key). */
  entitlement: z.string().optional(),
  /** PLAN_LIMIT_REACHED only: the organisation's plan key. */
  currentPlan: z.string().optional(),
  /** PLAN_LIMIT_REACHED only: the cheapest higher plan that includes it, null when none does. */
  upgradeTo: z.string().nullable().optional(),
  /** PLAN_LIMIT_REACHED only: what lifts the limit (a higher plan, or an extra seat). */
  suggestion: z.enum(["upgrade_plan", "add_seat"]).optional(),
  /** PHASE_HAS_UNAPPROVED_DELIVERABLES only: deliverables neither approved nor transmitted. */
  unfinished: z.int().min(0).optional(),
  /** MINUTES_EXIST only: the PV that already exists for the meeting or event. */
  minutesId: z.string().optional(),
  /** SHARE_LINK_PASSWORD_INVALID only: tries left before the link locks (GEDX-BE-04). */
  attemptsLeft: z.int().min(0).optional(),
  /** SHARE_LINK_LOCKED only: seconds until the link can be tried again. */
  retryAfterSeconds: z.int().min(1).optional()
});
const PROBLEM_TYPE_BASE = "https://batiflow.tn/problems/";
const problemType = (code) => `${PROBLEM_TYPE_BASE}${code.toLowerCase().replaceAll("_", "-")}`;
export {
  ERROR_CODES,
  ErrorCodeSchema,
  FieldErrorSchema,
  PROBLEM_TYPE_BASE,
  ProblemDetailsSchema,
  problemType
};
//# sourceMappingURL=errors.js.map